import type { Href } from "expo-router";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import { ActivityIndicator, Linking, Platform, ScrollView, Text, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { AppHeader } from "../../../../components/AppHeader";
import { Avatar, Badge, Button, Card, EmptyState, Section } from "../../../../components/ui";
import { getResumeSignedUrl } from "../../../../lib/profileUploads";
import { getStudentHistory, getStudentProfile } from "../../../../lib/studentProfile";
import type { StudentCompetitionHistoryRow, StudentProfileFull } from "../../../../types/database";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

async function openInNewTab(url: string) {
  if (Platform.OS === "web") window.open(url, "_blank", "noopener,noreferrer");
  else await Linking.openURL(url);
}

export default function CampusStudentDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const [profile, setProfile] = useState<StudentProfileFull | null>(null);
  const [history, setHistory] = useState<StudentCompetitionHistoryRow[]>([]);
  const [resumeUrl, setResumeUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      if (typeof id !== "string") return;
      setLoading(true);
      setErr(null);
      try {
        const [p, h] = await Promise.all([getStudentProfile(id), getStudentHistory(id)]);
        setProfile(p);
        setHistory(h);
        if (p?.resume_url) setResumeUrl(await getResumeSignedUrl(p.resume_url));
        else setResumeUrl(null);
      } catch (e) {
        setErr(e instanceof Error ? e.message : "Failed to load");
      }
      setLoading(false);
    })();
  }, [id]);

  const schoolLine = useMemo(() => {
    if (!profile) return "";
    return [profile.university, profile.major, profile.graduation_year, profile.year_in_school]
      .filter(Boolean)
      .join(" · ");
  }, [profile]);

  if (loading) {
    return (
      <SafeAreaView className="flex-1 items-center justify-center bg-linen">
        <ActivityIndicator size="large" color="#0A2540" />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1 bg-linen" edges={["top", "left", "right"]}>
      <ScrollView className="flex-1 px-4 pt-4" contentContainerStyle={{ paddingBottom: 28 }}>
        <View className="rounded-card border border-white/10 bg-forest px-2 py-2">
          <AppHeader />
        </View>
        <View className="mt-2 self-start">
        <Button
          size="sm"
          variant="ghost-light"
          onPress={() => {
            if (router.canGoBack()) router.back();
            else router.replace("/campus" as Href);
          }}
        >
          Back
        </Button>
        </View>
        {err ? <Text className="mb-2 text-sm text-red-600">{err}</Text> : null}

        <Card variant="light" padding="md">
          <Section overline="Student profile" title={profile?.full_name ?? "Student"} subtitle={schoolLine}>
            <></>
          </Section>
          <View className="flex-row items-start gap-4">
            <Avatar imageUrl={profile?.avatar_url} name={profile?.full_name ?? "Student"} size="lg" />
            <View className="flex-1">
              {profile?.linkedin_url ? (
                <Text className="mt-2 text-sm text-amber underline" onPress={() => void openInNewTab(profile.linkedin_url!)}>
                  LinkedIn
                </Text>
              ) : null}
              {resumeUrl ? (
                <Text className="mt-1 text-sm text-amber underline" onPress={() => void openInNewTab(resumeUrl)}>
                  Resume
                </Text>
              ) : null}
            </View>
          </View>
        </Card>

        <Text className="mt-6 text-lg font-semibold text-text">Competition history</Text>
        {history.length === 0 ? (
          <Card variant="light" padding="md"><EmptyState title="No history yet" /></Card>
        ) : history.map((row) => (
          <Card key={row.competition_id} variant="light" padding="md">
            <Text className="text-sm font-semibold text-text">{row.competition_name}</Text>
            <Text className="mt-0.5 text-xs text-text/60">{row.company_name}</Text>
            <Text className="mt-1 text-xs text-text/70">
              {formatDate(row.closes_at)} · Submitted {row.submitted_count}/{row.total_deliverables} ·{" "}
              {row.placement != null ? `Placed #${row.placement}` : "Pending"}
            </Text>
            <View className="mt-2 self-start">
              <Badge status={row.placement != null ? "success" : "grey"}>
                {row.placement != null ? `#${row.placement}` : "Pending"}
              </Badge>
            </View>
          </Card>
        ))}
      </ScrollView>
    </SafeAreaView>
  );
}
